import React, {useEffect, useState} from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Divider,
    Tooltip,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import {
    Flag as FlagIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Warning as WarningIcon,
} from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import {ApiDetails} from "../../../../Constants";
import {showSnackbar} from "../../../../components/SnackBar/SnackBar";
import {handle401Unauthorized, handle403Forbidden} from '../../../../utils/authErrorHandler';

const FLAG_CATEGORIES = [
    {value: "ABSCONDED", label: "Absconded", color: "#d32f2f"},
    {value: "MISBEHAVIOR", label: "Misbehavior", color: "#e65100"},
    {value: "PROPERTY_DAMAGE", label: "Property Damage", color: "#bf360c"},
    {value: "UNPAID_BALANCE", label: "Unpaid Balance", color: "#f9a825"},
    {value: "OTHER", label: "Other", color: "#757575"},
];

const getCategoryStyle = (category) => {
    const cat = FLAG_CATEGORIES.find(c => c.value === category);
    return cat ? {bgcolor: cat.color, color: "#fff"} : {bgcolor: "#757575", color: "#fff"};
};

const getCategoryLabel = (category) => {
    const cat = FLAG_CATEGORIES.find(c => c.value === category);
    return cat ? cat.label : category;
};

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const TenantFlags = ({tenantID, landlordID, propertyID, token, isVacated}) => {
    const [flags, setFlags] = useState([]);
    const [flagCount, setFlagCount] = useState(0);
    const [isFlagged, setIsFlagged] = useState(false);
    const [loading, setLoading] = useState(false);

    // Add flag modal
    const [addFlagModal, setAddFlagModal] = useState(false);
    const [flagData, setFlagData] = useState({category: "", reason: ""});
    const [savingFlag, setSavingFlag] = useState(false);

    // Remove flag modal
    const [removeFlagModal, setRemoveFlagModal] = useState(false);
    const [selectedFlag, setSelectedFlag] = useState(null);
    const [removalReason, setRemovalReason] = useState("");
    const [removingFlag, setRemovingFlag] = useState(false);

    useEffect(() => {
        fetchFlags();
    }, [tenantID]);

    const fetchFlags = () => {
        setLoading(true);
        fetch(ApiDetails + "pegasus/visionary/tenant/flags/get", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify({tenant_id: tenantID}),
        })
            .then(async (response) => {
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    const data = await response.json();
                    setFlags(data.flags || []);
                    setFlagCount(data.flag_count || 0);
                    setIsFlagged(data.is_flagged || false);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const handleAddFlag = () => {
        if (!flagData.category) {
            showSnackbar({
                title: "Error!",
                message: "Please select a category.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }
        if (!flagData.reason.trim()) {
            showSnackbar({
                title: "Error!",
                message: "Please provide a reason.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        setSavingFlag(true);

        fetch(ApiDetails + "pegasus/visionary/tenant/flags/add", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify({
                tenant_id: tenantID,
                landlord_id: landlordID,
                property_id: propertyID,
                category: flagData.category,
                reason: flagData.reason,
            }),
        })
            .then(async (response) => {
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Tenant flagged successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    setAddFlagModal(false);
                    setFlagData({category: "", reason: ""});
                    fetchFlags();
                } else {
                    const data = await response.json();
                    showSnackbar({
                        title: "Error!",
                        message: data.message || "Failed to flag tenant.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setSavingFlag(false);
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setSavingFlag(false);
            });
    };

    const handleRemoveFlag = () => {
        if (!removalReason.trim()) {
            showSnackbar({
                title: "Error!",
                message: "Please provide a reason for removal.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        setRemovingFlag(true);

        fetch(ApiDetails + "pegasus/visionary/tenant/flags/remove", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify({
                flag_id: selectedFlag.id,
                landlord_id: landlordID,
                property_id: propertyID,
                removal_reason: removalReason,
            }),
        })
            .then(async (response) => {
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Flag removed successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    setRemoveFlagModal(false);
                    setSelectedFlag(null);
                    setRemovalReason("");
                    fetchFlags();
                } else {
                    const data = await response.json();
                    showSnackbar({
                        title: "Error!",
                        message: data.message || "Failed to remove flag.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setRemovingFlag(false);
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setRemovingFlag(false);
            });
    };

    const openRemoveModal = (flag) => {
        setSelectedFlag(flag);
        setRemovalReason("");
        setRemoveFlagModal(true);
    };

    const handleClose = () => {
        setAddFlagModal(false);
        setRemoveFlagModal(false);
        setFlagData({category: "", reason: ""});
        setSelectedFlag(null);
        setRemovalReason("");
    };

    return (
        <Box>
            {/* Warning Banner */}
            {isFlagged && (
                <Alert
                    severity="error"
                    icon={<WarningIcon/>}
                    sx={{
                        mb: 3,
                        fontWeight: 500,
                        '& .MuiAlert-message': {fontWeight: 500}
                    }}
                >
                    This tenant has been flagged by {flagCount} other landlord(s).
                </Alert>
            )}

            {/* Flag Tenant Button */}
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 3}}>
                <Tooltip title={isVacated ? "Cannot flag a vacated tenant" : ""} arrow>
                    <span>
                        <Button
                            variant="contained"
                            startIcon={<FlagIcon/>}
                            onClick={() => setAddFlagModal(true)}
                            disabled={isVacated}
                            sx={{
                                bgcolor: '#d32f2f',
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                borderRadius: 2,
                                px: 3,
                                '&:hover': {
                                    bgcolor: '#b71c1c'
                                }
                            }}
                        >
                            Flag Tenant
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {/* Flags List */}
            {loading ? (
                <Box sx={{textAlign: 'center', py: 4}}>
                    <Typography>Loading flags...</Typography>
                </Box>
            ) : flags.length === 0 ? (
                <Box sx={{
                    textAlign: 'center',
                    py: 6,
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    border: '1px dashed #ccc'
                }}>
                    <FlagIcon sx={{fontSize: 48, color: '#ccc', mb: 2}}/>
                    <Typography variant="body1" color="textSecondary">
                        No flags on this tenant.
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {flags.map((flag) => (
                        <Grid item xs={12} key={flag.id}>
                            <Card sx={{
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                borderRadius: 2,
                                borderLeft: `4px solid ${getCategoryStyle(flag.category).bgcolor}`,
                            }}>
                                <CardContent>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        mb: 1
                                    }}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                                            <Chip
                                                label={getCategoryLabel(flag.category)}
                                                size="small"
                                                sx={{
                                                    ...getCategoryStyle(flag.category),
                                                    fontWeight: 600,
                                                    fontSize: '12px',
                                                }}
                                            />
                                            <Typography variant="body2" color="textSecondary">
                                                by {flag.flagged_by_name} on {formatDate(flag.created_at)}
                                            </Typography>
                                        </Box>
                                        {String(flag.flagged_by_landlord_id) === String(landlordID) && (
                                            <Tooltip title="Remove this flag">
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    startIcon={<DeleteIcon fontSize="small"/>}
                                                    onClick={() => openRemoveModal(flag)}
                                                    sx={{textTransform: 'none', fontSize: '13px'}}
                                                >
                                                    Remove
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Typography variant="body2" sx={{
                                        color: '#555',
                                        mt: 1,
                                        fontStyle: 'italic',
                                    }}>
                                        "{flag.reason}"
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Add Flag Modal */}
            <Dialog open={addFlagModal} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{color: '#d32f2f', fontWeight: 600}}>
                    Flag Tenant
                </DialogTitle>
                <DialogContent>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 2}}>
                        <FormControl fullWidth required>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={flagData.category}
                                label="Category"
                                onChange={(e) => setFlagData({...flagData, category: e.target.value})}
                            >
                                {FLAG_CATEGORIES.map((cat) => (
                                    <MenuItem key={cat.value} value={cat.value}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                            <Box sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                bgcolor: cat.color
                                            }}/>
                                            {cat.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Reason"
                            fullWidth
                            multiline
                            rows={4}
                            required
                            value={flagData.reason}
                            onChange={(e) => setFlagData({...flagData, reason: e.target.value})}
                            placeholder="Describe why you are flagging this tenant..."
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <LoadingButton
                        onClick={handleAddFlag}
                        loading={savingFlag}
                        startIcon={<FlagIcon/>}
                        variant="contained"
                        color="error"
                    >
                        Flag Tenant
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Remove Flag Modal */}
            <Dialog open={removeFlagModal} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{color: '#d32f2f', fontWeight: 600}}>
                    Remove Flag
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{mb: 2}}>Why are you removing this flag?</Typography>
                    <TextField
                        label="Reason"
                        fullWidth
                        multiline
                        rows={3}
                        required
                        value={removalReason}
                        onChange={(e) => setRemovalReason(e.target.value)}
                        placeholder="e.g., Tenant cleared their outstanding balance"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <LoadingButton
                        onClick={handleRemoveFlag}
                        loading={removingFlag}
                        startIcon={<DeleteIcon/>}
                        variant="contained"
                        color="error"
                    >
                        Remove Flag
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TenantFlags;
